import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCenter } from "@/context/CenterContext";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const { activeId } = useCenter();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);

  const emit = (payload) => listenersRef.current.forEach((cb) => cb(payload));
  const activeIdRef = useRef(activeId);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch (err) { console.error("Failed to load notifications", err); }
  }, []);

  // reload notifications when the active center changes
  useEffect(() => { if (user && activeId) loadNotifications(); }, [activeId, user, loadNotifications]);

  const send = useCallback((obj) => {
    if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.send(JSON.stringify(obj));
  }, []);

  useEffect(() => {
    if (!user) return;
    loadNotifications();
    let alive = true;
    let ws;
    const connect = () => {
      ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => { setConnected(false); if (alive) setTimeout(connect, 3000); };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.event === "notification") {
            if (!msg.data.center_id || msg.data.center_id === activeIdRef.current) {
              setNotifications((prev) => [msg.data, ...prev]);
            }
          }
          emit(msg);
        } catch (err) { console.debug("Ignoring malformed WS message", err); }
      };
    };
    connect();
    const ping = setInterval(() => send({ event: "ping" }), 25000);
    return () => { alive = false; clearInterval(ping); if (ws) ws.close(); };
  }, [user, loadNotifications, send]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllRead = async () => {
    await api.put("/notifications/read-all");
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };
  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  return (
    <RealtimeContext.Provider value={{ subscribe, send, notifications, unreadCount, markAllRead, markRead, connected, loadNotifications }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeContext);
